import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  acceptInvitationByToken,
  declineInvitationByToken,
  fetchMyCompany,
  fetchMyInvitations,
} from "../api/companyWorkspace";
import {
  committedEnrollmentStatus,
  errorCode,
  invitationActionableForRecipient,
  invitationStatusLabel,
  roleLabel,
} from "../lib/companyLabels";
import { createScopeGate, sameActorId } from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function CompanyMyInvitationsContent() {
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setBusyId(null);
    }
  };

  const load = useCallback(({ background = false } = {}) => {
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
    }
    return fetchMyInvitations()
      .then((data) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setRows(Array.isArray(data) ? data : []);
        setAuthorityActorId(actorId);
        revalidationConfirmed = true;
        setActionError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return;
        }
        setAuthorityActorId(null);
        setError(messageFromError(err));
        setState("error");
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ actorId, companyId: null });
    busyOpRef.current = null;
    setBusyId(null);
    setActionError(null);
    setRows([]);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const act = async (invitation, kind) => {
    if (!invitation?.token || busyOpRef.current || writesSuspendedRef.current) return;
    if (!invitationActionableForRecipient(invitation)) return;
    const op = scopeGate.beginMutation({
      actorId,
      resourceId: invitation.id,
    });
    busyOpRef.current = op;
    setBusyId(invitation.id);
    setActionError(null);
    try {
      if (kind === "accept") {
        await acceptInvitationByToken(invitation.token);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await fetchMyCompany();
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        navigate("/companies/mine", { replace: true });
        return;
      }
      await declineInvitationByToken(invitation.token);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const code = errorCode(err);
      const invitationStatus = committedEnrollmentStatus(err, "invitation_status");
      if (code === "already_member" || code === "membership_suspended") {
        setActionError(
          invitationStatus
            ? `Запрошення вже недійсне (${invitationStatusLabel(invitationStatus)}). Успішний вступ не відбувся.`
            : "Запрошення вже недійсне. Успішний вступ не відбувся.",
        );
      } else if (code === "company_enrollment_conflict") {
        setActionError(
          "У вас уже є поточне членство в іншій компанії. Спочатку вийдіть або передайте його — автоматичне перемикання заборонене.",
        );
      } else {
        setActionError(messageFromError(err));
      }
      await load();
    } finally {
      releaseBusy(op);
    }
  };

  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);

  if (state === "loading" || (state === "ready" && !actorMatches)) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження запрошень" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Помилка"
          description={error || "Спробуйте пізніше."}
          size="md"
        />
        <button type="button" className="action-btn" onClick={load}>
          Повторити
        </button>
      </div>
    );
  }

  if (!actorMatches) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження запрошень" />
      </div>
    );
  }

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container">
        <CompanyPageHeader
          title="Мої запрошення"
          subtitle="Вхідні запрошення до компаній. Токен не береться зі сповіщень."
          actions={
            <Link to="/companies/mine" className="action-btn action-btn--ghost">
              Моя компанія
            </Link>
          }
        />
        {actionError ? <InlineError>{actionError}</InlineError> : null}
        {rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-inbox"
            title="Немає активних запрошень"
            description="Коли вас запросять, запрошення з’явиться тут."
            size="md"
          />
        ) : (
          <ul className="feature-company-workspace__list">
            {rows.map((invitation) => {
              const actionable = invitationActionableForRecipient(invitation);
              return (
                <li key={invitation.id} className="feature-company-workspace__card">
                  <div className="feature-company-workspace__card-top">
                    <div>
                      <h2 className="feature-company-workspace__long-value">
                        {invitation.company?.name || "Компанія"}
                      </h2>
                      <p>
                        Роль: {roleLabel(invitation.role)} · статус:{" "}
                        {invitationStatusLabel(invitation.status)}
                      </p>
                    </div>
                    <div className="feature-company-workspace__inline-actions">
                      {invitation.token ? (
                        <Link
                          to={`/companies/invitations/by-token/${invitation.token}`}
                          className="action-btn action-btn--ghost"
                        >
                          Відкрити
                        </Link>
                      ) : null}
                      {actionable ? (
                        <>
                          <button
                            type="button"
                            className="action-btn action-btn--accent"
                            disabled={busyId === invitation.id || !invitation.token}
                            onClick={() => act(invitation, "accept")}
                          >
                            Прийняти
                          </button>
                          <button
                            type="button"
                            className="action-btn action-btn--danger"
                            disabled={busyId === invitation.id || !invitation.token}
                            onClick={() => act(invitation, "decline")}
                          >
                            Відхилити
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default CompanyMyInvitationsContent;
